import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Select, Button, Input, BasicIconButton } from 'components';
import DND from 'pages/createform/DNDForm';
import { fieldOptionList } from 'assert/OptionList';
import { Notification } from 'components/CustomNotification';
import { t } from 'i18next';
import { DialogActions, Divider, Tooltip } from '@mui/material';
import { TemplatesStyle } from './CreateForm.style';
import CheckBox from 'components/CheckBox';
import clsx from 'clsx';
import { ReactComponent as Reset } from 'assert/images/Jobs/refresh.svg';

export default function CustomForm({ closeModal, handleSelectTemplate, jobDetail }) {
  const templateRef = useRef();
  const [element, setElement] = useState([]);
  const [fieldOptions, setFieldOptions] = useState('');
  const [OptionList, setOptionList] = useState([]);
  const [editIndex, setEditIndex] = useState('');
  const [initialState, setInitialState] = useState(true);
  const [fieldOption, setFieldOption] = useState(fieldOptionList || []);
  const [fieldValue, setFieldValue] = useState({
    id: '',
    question: '',
    field: '',
    required: false,
    options: [],
  });

  useEffect(() => {
    if (jobDetail?.[0]?.template) {
      setElement(JSON.parse(jobDetail?.[0]?.template));
    } else {
      setElement([{
        "id": "item-0",
        "question": "Email",
        "field": "Free Text",
        "required": true,
        "options": [],
        "chosen": false,
        "selected": false,
        "disableActionForEmail": true
      }])
    }
  }, [jobDetail]);

  useEffect(() => {
    let fieldNum = 0;
    element?.forEach((li) => {
      if (li.field === "File Upload") {
        fieldNum += 1;
      }
    });
    if (fieldNum >= 3) {
      fieldOption[4].disabled = true;
    } else {
      fieldOption[4].disabled = false;
    }
    setFieldOption((prevState) => [...prevState]);
  }, [element]);

  useEffect(() => {
    const list = [];
    fieldOptions?.split("&&").forEach((data) => {
      if (data?.trim()) {
        // "option_" + index
        list.push({ value: data?.trim(), label: data?.trim(), disabled: true });
      }
    });
    setOptionList(list);
  }, [fieldOptions, fieldValue.field]);

  const handleCancel = useCallback(() => {
    setFieldValue({
      id: '',
      question: '',
      field: '',
      required: false,
      options: [],
    });
    setFieldOptions('');
    setEditIndex('');
    setInitialState(true);
  }, []);

  const handleFieldValue = useCallback((data, name) => {
    setFieldValue((prevState) => ({
      ...prevState,
      [name]: data?.target?.value || data,
    }));
  }, []);

  const handleAdd = useCallback(() => {
    let count = 0;
    element?.filter((it) => {
      if (element.length + 1 <= Number(it.id.replace('item-', ''))) {
        count = Number(it.id.replace('item-', '')) + 1;
      } else {
        count = element.length;
      }
      return null;
    });
    setElement([
      ...element,
      {
        id: `item-${count}`,
        question: fieldValue.question,
        field: fieldValue.field,
        required: fieldValue.required,
        options: OptionList,
      },
    ]);
    handleCancel();
  }, [OptionList, element, fieldValue, handleCancel]);

  const handleEdit = useCallback(
    (editId) => {
      templateRef.current.scrollTop = 0;
      const filteredEle = element.filter((item) => editId === item.id);
      const { id, question, field, required, options } = filteredEle?.[0];
      setFieldValue({
        id,
        question,
        field,
        required,
        options,
      });
      const editOptions = options.map((li) => li?.label?.trim()).join('&& ');
      setFieldOptions(editOptions);
      setEditIndex(editId);
      setInitialState(false);
    },
    [element]
  );

  const handleUpdate = useCallback(() => {
    const editedForm = [];
    element.forEach((item) => {
      if (item.id === editIndex) {
        editedForm.push({
          id: editIndex,
          question: fieldValue.question,
          field: fieldValue.field,
          required: fieldValue.required,
          options: OptionList,
        });
      } else {
        editedForm.push(item);
      }
    });

    setElement(editedForm);
    handleCancel();
  }, [OptionList, editIndex, element, fieldValue, handleCancel]);

  const handleDelete = useCallback(
    (deleteId) => {
      const filteredData = element?.filter((it) => it.id !== deleteId);
      setElement(filteredData);
    },
    [element]
  );

  const Submit = () => {
    if (element?.length > 0) {
      handleSelectTemplate(element);
    } else {
      Notification('info', 'Add atleast one field');
    }
  };

  const isOptionFieldOpen =
    fieldValue.field === 'Radio Button' || fieldValue.field === 'Dropdown';
  const isBtnDisabled =
    !fieldValue.question ||
    !fieldValue.field ||
    !(isOptionFieldOpen ? Boolean(fieldOptions) : true) ||
    element?.length >= 15;
  return (
    <TemplatesStyle ref={templateRef}>
      <div className="custom-field-wrap-container">
        <Input
          required
          multiline
          label={t('Job.YourQuestion')}
          onChange={(data) => handleFieldValue(data, 'question')}
          value={fieldValue.question}
          className={'job-question-field'}
          maxLimit={200}
        />
        <div className="custom-field-wrap">
          <Select
            required
            Options={fieldOption}
            infoIndex={4}
            infoText={"Can't upload more than 3 files"}
            label={t('Job.Fields')}
            onChange={(data) => handleFieldValue(data, 'field')}
            value={fieldValue.field}
          />
          <CheckBox
            label={t('Job.RequiredField')}
            onChange={(data) => handleFieldValue(data, 'required')}
            value={fieldValue.required}
            size="small"
          />
        </div>
      </div>
      <div
        className={clsx(
          'custom-option-container',
          !isOptionFieldOpen && 'custom-without-option-container'
        )}
      >
        {isOptionFieldOpen && (
          <div className="custom-option-field">
            <Input
              multiline
              required
              onChange={setFieldOptions}
              label={clsx(
                fieldValue.field === 'Radio Button'
                  ? 'Radio'
                  : fieldValue.field === 'Dropdown'
                  ? 'Dropdown'
                  : '',
                'Options'
              )}
              value={fieldOptions}
              placeholder={"e.g. option1 && option2"}
            />
            <span className="helper-text">
              Use && to add Options e.g. Options1 && Options2.
            </span>
          </div>
        )}
        <div className="custom-btn-container">
          {editIndex || editIndex === 0 ? (
            <>
              <Button
                disabled={isBtnDisabled}
                variant="outlined"
                label={t('Job.Update')}
                onClick={handleUpdate}
              />
              <Button
                variant="outlined"
                label={t('Job.Cancel')}
                onClick={handleCancel}
              />
            </>
          ) : (
            <Button
              variant="outlined"
              label={t('Job.Add')}
              disabled={isBtnDisabled}
              onClick={handleAdd}
            />
          )}
        </div>
      </div>
      {element.length > 0 && (
        <div className="custom-preview-container">
          <Divider />
          <h3>
            {t('Job.Preview')}
            <Tooltip title="Delete all" placement="right">
              <span>
                <BasicIconButton
                  className="custom-reset-form"
                  icon={<Reset />}
                  size="small"
                  onClick={() => {
                    setElement([{
                      "id": "item-0",
                      "question": "Email",
                      "field": "Free Text",
                      "required": true,
                      "options": [],
                      "chosen": false,
                      "selected": false,
                      "disableActionForEmail": true
                    }])
                    handleCancel();
                  }}
                />
              </span>
            </Tooltip>
          </h3>
          <DND
            questions={element}
            initialState={initialState}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleDnd={setElement}
          />
        </div>
      )}
      <DialogActions>
        <Button variant="text" label={t('Job.SelectCustomTemplate')} onClick={Submit} />
        <Button variant="text" label={t('Job.Cancel')} onClick={() => closeModal()} />
      </DialogActions>
    </TemplatesStyle>
  );
}
